import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TextInput from "../../elements/TextInput.jsx";
import {
	acceptBudgetInvite, changeActiveBudget, endBudgetShareByEmail,
	getSharedBudgets,
	rejectBudgetInvite,
	shareBudgetWithEmail
} from "../../../redux/actions/sharing.js";
import Button from "../../components/Button.jsx";
import CircleLoader from "../../components/CircleLoader.jsx";
import Card from "../../components/Card.jsx";
import UserNameComponent from "../../components/UserNameComponent.jsx";
import useLoggedInUser from "../../hooks/useLoggedInUser.js";


export default function AccountPageSharing() {
	const submittingShareBudget = useSelector((state) => state.sharing.submittingShareBudget);
	const player = useLoggedInUser();
	const budgets = useSelector((state) => state.sharing.budgets);
	const activeBudget = useSelector((state) => state.settings.activeBudget);

	const changingActiveBudgetId = useSelector((state) => state.sharing.changingActiveBudgetId);
	const acceptingInviteBudgetId = useSelector((state) => state.sharing.acceptingInviteBudgetId);
	const rejectingInviteBudgetId = useSelector((state) => state.sharing.rejectingInviteBudgetId);

	const [shareBudgetInput, updateShareBudgetInput] = useState('');
	const dispatch = useDispatch();
	const shareBudgetTextChange = (e) => {
		updateShareBudgetInput(e.target.value);
	};

	const canSubmitForm = shareBudgetInput !== '' && !submittingShareBudget;

	const formSubmit = (e) => {
		e.preventDefault();
		if(!canSubmitForm) {
			return;
		}
		dispatch(shareBudgetWithEmail(activeBudget.id, shareBudgetInput))
			.then(() => {
				updateShareBudgetInput('');
			});
	};

	useEffect(() => {
		dispatch(getSharedBudgets());
	}, []);


	const { inviting, invited, activeBudgets } = useMemo(() => {
		const invited = [];
		const inviting = [];
		const activeBudgets = [];
		const theReturner = { inviting, invited, activeBudgets };
		if(!budgets) {
			return theReturner;
		}
		for(let budget of budgets) {
			if(budget.owner.id === player.id) {
				activeBudgets.push(budget);
				for(let share of budget.shared_with) {
					if(share.invited && share.invited.id !== player.id) {
						inviting.push({ ...share.invited, budget_id: budget.id });
					}
				}
			} else {

				for(let share of budget.shared_with) {
					if(share.invited && share.invited.id === player.id) {
						invited.push(share);
						if(share.invite_status === 1) {
							activeBudgets.push(budget);
						}
					}
				}
			}
		}
		return theReturner;
	}, [budgets]);

	if(!activeBudget) {
		return <CircleLoader />;
	}

	return (
		<div>
			<div className='row'>
				<div className='col s12 m6'>
					<h5>Share Budget
						<div className='hint_text email'>
							<UserNameComponent
								user={activeBudget.owner}
							/>
						</div>
					</h5>
					<form
						onSubmit={formSubmit}
					>
						<div className='input-field'>
							<TextInput
								disabled={submittingShareBudget}
								type='email'
								label='Email Address'
								onChange={shareBudgetTextChange}
								value={shareBudgetInput}
							/>
						</div>
						<Button
							disabled={!canSubmitForm}
							type='submit'
						>
							Share
						</Button>
					</form>
					{inviting.length > 0 &&
					<div className='inviting'>
						<h5>Inviting</h5>
						<div className='collection'>

							{inviting.map((invitedPerson) => (
								<div
									key={invitedPerson.id}
									className='collection-item'
								>
									<span>
										Sharing Budget with{' '}
										<UserNameComponent
											user={invitedPerson}
										/>
									</span>
									<div className='secondary-content'>
										<Button
											onClick={() => {
												dispatch(endBudgetShareByEmail(invitedPerson.budget_id, invitedPerson.email));
											}}
										>
											Stop Sharing
										</Button>
									</div>
								</div>
							))}
						</div>
					</div>
					}
				</div>
				<div className='col s12 m6'>
					{!budgets &&
					<CircleLoader />
					}
					{activeBudgets.length > 0 &&
					<div>
						<h5>Active Budgets</h5>
						{activeBudgets.length > 0 &&
						<div className='collection'>
							{activeBudgets.map((budget) => (
								<div
									className='collection-item'
									key={budget.id}>
									<span>Budget started by{' '}
										<UserNameComponent
											user={budget.owner}
											selfReference
										/>
										- {budget.name}
									</span>
									<div className='secondary-content'>
										<ChangeActiveBudgetButton
											budget={budget}
											changingActiveBudgetId={changingActiveBudgetId}
											onClick={() => {
												dispatch(changeActiveBudget(budget.id));
											}}
											activeBudget={player.active_budget}
										/>
									</div>
								</div>

							))}
						</div>
						}
					</div>
					}
					{invited.length > 0 &&
					<div className='invites'>
						<h5>Invites</h5>
						<div className='collection'>
							{invited.map((share) => (
								<div
									className='collection-item'
									key={share.budget_id}>
									{share.id}
									<UserNameComponent
										user={share.owner}
									/>
									<div className='secondary-content space-between'>
										<Button
											disabled={share.invite_status === 1 || acceptingInviteBudgetId === share.budget_id}
											onClick={() => {
												dispatch(acceptBudgetInvite(share.budget_id));
											}}
										>
											Accept
										</Button>
										<Button
											danger
											disabled={share.invite_status === -1 || rejectingInviteBudgetId === share.budget_id}
											onClick={() => {
												dispatch(rejectBudgetInvite(share.budget_id));
											}}
										>
											Reject
										</Button>
									</div>
								</div>
							))}
						</div>
					</div>
					}
				</div>
			</div>
		</div>
	);
}

function ChangeActiveBudgetButton({ changingActiveBudgetId, activeBudget, budget, onClick }) {
	let text = null;
	const isBeingSelected = changingActiveBudgetId === budget.id;
	const isSelected = activeBudget.id === budget.id;
	if(isBeingSelected) {
		text = 'Changing...';
	} else if(activeBudget.id === budget.id) {
		text = 'Active';
	} else {
		text = 'Switch To Budget';
	}
	return (
		<Button
			onClick={onClick}
			disabled={isBeingSelected || isSelected}
		>
			<span>{text}</span>
		</Button>
	);
}